import z from "zod";
import { VISION_DEPLOYMENT_DEFAULT, VISION_GEMINI_MODEL_LABELS, VISION_GEMINI_MODELS, VISION_GEMINI_THINKING_LEVELS, VISION_OPENAI_REASONING_EFFORTS, VisionLlmProviderEnum, } from "../vision/vision.schema";
export const FEATURE_FLAGS = [
    {
        type: "boolean",
        key: "reporting",
        label: "Reporting",
        description: "Shows the Reports tab on the Run page.",
        default: true,
    },
    {
        type: "boolean",
        key: "restricted",
        label: "Restricted (overdue payment)",
        description: "Warns every member that the account is overdue and blocks camera deployment. " +
            "Enforced at the relay, so it holds for relay-connected devices no matter what " +
            "the browser does; a project reaching a camera by local address is only gated in " +
            "the UI, because nothing sits between the browser and the local network. Stopping " +
            "a running dashboard stays available, and nothing already deployed is torn down.",
        default: false,
    },
    {
        type: "boolean",
        key: "localCameraAccess",
        label: "Local camera access",
        description: "Lets projects reach a camera by typing its address on the local network, bypassing the relay. Off by default: a direct address is unauthenticated and unencrypted, and nothing checks that the camera belongs to this organization.",
        default: false,
    },
    {
        type: "number",
        nullable: true,
        key: "maxTrainings",
        label: "Monthly training limit",
        description: "Maximum trainings this organization can start per calendar month (UTC). Leave empty for unlimited.",
        default: null,
        nullLabel: "Unlimited",
        min: 0,
        max: 10000,
    },
    {
        type: "enum",
        key: "visionProvider",
        label: "Robopipe Vision provider",
        description: "Model for auto-annotation. Falls back to the deployment default if this server lacks its " +
            "credentials.",
        default: VISION_DEPLOYMENT_DEFAULT,
        options: [
            { value: VISION_DEPLOYMENT_DEFAULT, label: "Deployment default" },
            { value: VisionLlmProviderEnum.GEMINI, label: "Gemini" },
            { value: VisionLlmProviderEnum.OPENAI, label: "OpenAI (GPT-6.1 Sol)" },
        ],
    },
    {
        type: "enum",
        key: "visionOpenaiEffort",
        label: "Robopipe Vision OpenAI effort",
        description: "OpenAI only. Higher is slower and costlier; xhigh and max may time out.",
        default: VISION_DEPLOYMENT_DEFAULT,
        options: [
            { value: VISION_DEPLOYMENT_DEFAULT, label: "Deployment default" },
            ...VISION_OPENAI_REASONING_EFFORTS.map((value) => ({ value, label: value })),
        ],
    },
    {
        type: "enum",
        key: "visionGeminiThinking",
        label: "Robopipe Vision Gemini thinking",
        description: "Gemini only. High may time out on dense frames.",
        default: VISION_DEPLOYMENT_DEFAULT,
        options: [
            { value: VISION_DEPLOYMENT_DEFAULT, label: "Deployment default" },
            ...VISION_GEMINI_THINKING_LEVELS.map((value) => ({ value, label: value })),
        ],
    },
    {
        type: "enum",
        key: "visionGeminiModel",
        label: "Robopipe Vision Gemini model",
        description: "Gemini only. Gemini 4 Argon costs more per run and may run longer; switching ends open " +
            "conversations.",
        default: VISION_DEPLOYMENT_DEFAULT,
        options: [
            { value: VISION_DEPLOYMENT_DEFAULT, label: "Deployment default" },
            ...VISION_GEMINI_MODELS.map((value) => ({ value, label: VISION_GEMINI_MODEL_LABELS[value] })),
        ],
    },
];
export const FEATURE_FLAG_DEFS = FEATURE_FLAGS;
export const getFeatureFlagDef = (key, defs = FEATURE_FLAG_DEFS) => defs.find((def) => def.key === key);
export const storedFeatureFlagsSchema = z
    .record(z.string(), z.union([z.boolean(), z.number(), z.string(), z.null()]))
    .default({});
const describeExpected = (def) => {
    switch (def.type) {
        case "boolean":
            return "a boolean";
        case "number":
            return def.nullable
                ? `a number, or null for "${def.nullLabel}"`
                : "a number";
        case "string":
            return "a string";
        case "enum":
            return `one of: ${def.options.map((option) => option.value).join(", ")}`;
    }
};
const validateAgainstRegistry = (flags, ctx, defs) => {
    for (const [key, value] of Object.entries(flags)) {
        const def = getFeatureFlagDef(key, defs);
        if (!def) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Unknown feature flag "${key}"`,
                path: [key],
            });
            continue;
        }
        if (!isValidFlagValue(def, value)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Feature flag "${key}" expects ${describeExpected(def)}`,
                path: [key],
            });
            continue;
        }
        if (def.type === "number" && typeof value === "number") {
            if (def.min !== undefined && value < def.min) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Feature flag "${key}" must be at least ${def.min}`,
                    path: [key],
                });
            }
            if (def.max !== undefined && value > def.max) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Feature flag "${key}" must be at most ${def.max}`,
                    path: [key],
                });
            }
        }
    }
};
export const buildFeatureFlagsSchema = (defs = FEATURE_FLAG_DEFS) => storedFeatureFlagsSchema.superRefine((flags, ctx) => validateAgainstRegistry(flags, ctx, defs));
export const featureFlagsUpdateSchema = buildFeatureFlagsSchema();
export const resolveFeatureFlagsRecord = (stored, defs = FEATURE_FLAG_DEFS) => {
    const resolved = {};
    for (const def of defs) {
        const value = stored?.[def.key];
        resolved[def.key] = isValidFlagValue(def, value) ? value : def.default;
    }
    return resolved;
};
export const resolveFeatureFlags = (stored) => resolveFeatureFlagsRecord(stored);
export const pruneFeatureFlags = (draft, defs = FEATURE_FLAG_DEFS) => {
    const pruned = {};
    for (const def of defs) {
        const value = draft[def.key];
        if (isValidFlagValue(def, value) && value !== def.default) {
            pruned[def.key] = value;
        }
    }
    return pruned;
};
const isValidFlagValue = (def, value) => {
    switch (def.type) {
        case "boolean":
            return typeof value === "boolean";
        case "number":
            if (value === null)
                return def.nullable === true;
            return typeof value === "number" && Number.isFinite(value);
        case "string":
            return typeof value === "string";
        case "enum":
            return (typeof value === "string" && def.options.some((option) => option.value === value));
    }
};
//# sourceMappingURL=feature-flags.js.map